"use client";

import { Paywall } from "@/components/paywall";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { useAIDisclaimer } from "@/contexts/ai-disclaimer-context";
import { useProUser } from "@/contexts/pro-user-context";
import { usePropertyChat } from "@/contexts/property-chat-context";
import { authenticatedFetch } from "@/lib/api-middleware";
import { cn } from "@/lib/utils";
import { useChat } from "ai/react";
import { ChevronDown, MessageSquare, RefreshCcw, Send, X } from "lucide-react";
import { useState } from "react";

function LoadingDots() {
  return (
    <div className="flex gap-1">
      <span className="animate-bounce delay-0">•</span>
      <span className="animate-bounce delay-100">•</span>
      <span className="animate-bounce delay-200">•</span>
    </div>
  );
}

const PROPERTY_SUGGESTIONS = [
  "What are the biggest advantages of this property?",
  "What are the potential risks I should be aware of?",
  "How does the price compare to similar properties?",
  "What's the neighborhood like?",
  "What improvements might this property need?",
  "Is this a good investment property?",
];

interface EmptyStateProps {
  address?: string;
  onSuggestionClick: (text: string) => void;
}

function EmptyState({ address, onSuggestionClick }: EmptyStateProps) {
  return (
    <div className="flex flex-col items-center justify-center h-full p-8 text-center">
      <div className="w-16 h-16 bg-canary/10 rounded-full flex items-center justify-center mb-6">
        <MessageSquare className="w-8 h-8 text-canary" />
      </div>
      <h3 className="text-xl font-semibold mb-2">
        Chat with {address || "This Property"}
      </h3>
      <p className="text-muted-foreground mb-6 max-w-sm">
        I'm your AI property expert, ready to analyze every detail of{" "}
        {address || "this home"}. From market value to neighborhood insights,
        I'll help you make a confident decision.
      </p>
      <div className="flex flex-col gap-2 w-full max-w-sm">
        <Button
          variant="outline"
          className="flex items-center text-left w-full py-3 px-4 h-auto"
          onClick={() => onSuggestionClick("What makes this property special?")}
        >
          <span className="text-base mr-2">✨</span>
          <span className="text-sm">What makes this property special?</span>
        </Button>
        <Button
          variant="outline"
          className="flex items-center text-left w-full py-3 px-4 h-auto"
          onClick={() => onSuggestionClick("What are the biggest risks?")}
        >
          <span className="text-base mr-2">🎯</span>
          <span className="text-sm">What should I watch out for?</span>
        </Button>
      </div>
    </div>
  );
}

interface PropertyChatPanelProps {
  address?: string;
}

export function PropertyChatPanel({ address }: PropertyChatPanelProps) {
  const { isOpen, closeChat, propertyId } = usePropertyChat();
  const { isPro } = useProUser();
  const { showDisclaimer } = useAIDisclaimer();
  const [isShowingSuggestions, setIsShowingSuggestions] = useState(false);

  const {
    messages,
    input,
    handleInputChange,
    handleSubmit: originalHandleSubmit,
    setMessages,
    setInput,
    isLoading,
  } = useChat({
    api: "/api/property-chat",
    body: { propertyId },
    fetch: authenticatedFetch,
  });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const acknowledged = await showDisclaimer();
    if (!acknowledged) return;
    await originalHandleSubmit(e);
  };

  const handleClearMessages = () => {
    setMessages([]);
    setInput("");
  };

  const handleSuggestionClick = (text: string) => {
    setInput(text);
    setIsShowingSuggestions(false);
  };

  return (
    <Sheet open={isOpen} onOpenChange={closeChat}>
      <SheetContent
        side="right"
        className="w-[400px] border-l p-0 bg-slate-50 sm:max-w-[500px]"
      >
        {!isPro ? (
          <Paywall
            title="Your Personal Property Advisor"
            description="Get instant, expert-level insights about this property from an AI that analyzes every detail - from price trends to hidden potential."
            features={[
              "Deep property analysis with real-time market data",
              "Uncover hidden opportunities and potential issues",
              "Compare with similar properties in the area",
              "Get renovation and improvement recommendations",
              "Understand the true value and investment potential",
            ]}
          />
        ) : (
          <div className="flex h-full flex-col">
            {/* Header */}
            <div className="flex items-center justify-between border-b bg-white px-4 py-3">
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 bg-canary/10 rounded-lg flex items-center justify-center">
                  <MessageSquare className="h-5 w-5 text-canary" />
                </div>
                <div>
                  <h2 className="font-semibold">Property Advisor</h2>
                  {address && (
                    <p className="text-xs text-muted-foreground">
                      Analyzing {address}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {messages.length > 0 && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={handleClearMessages}
                  >
                    <RefreshCcw className="h-4 w-4" />
                  </Button>
                )}
                <Button variant="ghost" size="icon" onClick={closeChat}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.length === 0 ? (
                <EmptyState
                  address={address}
                  onSuggestionClick={handleSuggestionClick}
                />
              ) : (
                messages.map((message) => (
                  <div
                    key={message.id}
                    className={cn(
                      "flex",
                      message.role === "assistant"
                        ? "justify-start"
                        : "justify-end",
                    )}
                  >
                    <div
                      className={cn(
                        "rounded-lg px-3 py-2 text-sm max-w-[85%]",
                        message.role === "assistant"
                          ? "bg-gray-100 text-smokeyText"
                          : "bg-canary text-smokeyText",
                      )}
                    >
                      {message.content}
                    </div>
                  </div>
                ))
              )}

              {isLoading && (
                <div className="flex gap-2">
                  <div className="rounded-lg bg-gray-100 px-3 py-2 text-sm text-smokeyText">
                    <LoadingDots />
                  </div>
                </div>
              )}
            </div>

            {/* Input area */}
            <div className="border-t bg-white p-4">
              <form onSubmit={handleSubmit} className="flex gap-2">
                <Input
                  value={input}
                  onChange={handleInputChange}
                  placeholder="Ask about this property..."
                  className="flex-1"
                  disabled={isLoading}
                />
                <Button
                  type="submit"
                  size="icon"
                  disabled={!input.trim() || isLoading}
                  className="h-10 w-10 bg-canary text-smokeyBlack disabled:opacity-50 disabled:cursor-not-allowed hover:bg-canary/80"
                >
                  <Send className="h-4 w-4" />
                </Button>
              </form>

              {/* Suggestions */}
              <div className="mt-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full gap-2"
                  onClick={() => setIsShowingSuggestions(!isShowingSuggestions)}
                >
                  <ChevronDown
                    className={cn(
                      "h-4 w-4 transition-transform",
                      isShowingSuggestions && "rotate-180",
                    )}
                  />
                  Suggested questions
                </Button>

                {isShowingSuggestions && (
                  <div className="mt-2 flex flex-col gap-2 max-h-48 overflow-y-auto">
                    {PROPERTY_SUGGESTIONS.map((suggestion) => (
                      <Button
                        key={suggestion}
                        variant="outline"
                        className="w-full justify-start text-left text-sm whitespace-normal h-auto"
                        onClick={() => handleSuggestionClick(suggestion)}
                      >
                        {suggestion}
                      </Button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
